// The socket (module 09 DESIGN: a WebSocket from Cobrain's own server). One connection per
// page, authenticated by the same cookie as every fetch, reconnecting with a backing-off
// delay when it drops. It only listens: every change a person makes goes through the API,
// and the server tells the room. A page that is hidden keeps its socket, so a chat arriving
// while another tab is in front still moves the red count.
import { useViewing } from '../view-as.js';
import { useEffect, useRef } from 'react';
import type { MessengerEvent } from './api.js';

/** The close code the server sends every socket as it stops to restart (RFC 6455 1001, "going away"). */
export const GOING_AWAY = 1001;
/** How long after a restart's close the first reconnect waits: the restart, measured, plus room. */
export const AFTER_RESTART_MS = 6000;

export function socketUrl(store: string | null): string {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/ws/messenger${store ? `?store=${encodeURIComponent(store)}` : ''}`;
}

/** Subscribes for the life of the component; `onEvent` is read through a ref so the socket never reconnects because a handler changed. */
export function useMessengerSocket(store: string | null, onEvent: (e: MessengerEvent) => void, onState?: (open: boolean) => void): void {
  const viewing = useViewing();
  const handler = useRef(onEvent);
  handler.current = onEvent;
  const stateRef = useRef(onState);
  stateRef.current = onState;
  useEffect(() => {
    if (viewing) return;
    let ws: WebSocket | null = null;
    let closed = false;
    let attempt = 0;
    let timer: number | undefined;
    const connect = () => {
      if (closed) return;
      try { ws = new WebSocket(socketUrl(store)); } catch { schedule(); return; }
      ws.onopen = () => { attempt = 0; stateRef.current?.(true); };
      ws.onmessage = (m) => {
        if (typeof m.data !== 'string' || m.data === 'pong') return;
        try { handler.current(JSON.parse(m.data) as MessengerEvent); } catch { /* not ours */ }
      };
      // BUGS.md D17: a close with 1001 is the server going away to restart (api ws/messenger-ws.ts closeMessengerWs),
      // which on the droplet takes about two and a half seconds to listen again (8 Oct, 09:09:03 to 09:09:05.46 UTC).
      // The first try came at two seconds, met Caddy's 502 while nothing listened, and the browser logged
      // "WebSocket connection ... failed" on every open page at every deploy. After a restart, wait past it.
      ws.onclose = (e) => { stateRef.current?.(false); schedule(e.code === GOING_AWAY ? AFTER_RESTART_MS : 0); };
      ws.onerror = () => { ws?.close(); };
    };
    const schedule = (atLeast = 0) => {
      if (closed) return;
      attempt++;
      timer = window.setTimeout(connect, Math.max(atLeast, Math.min(30_000, 1000 * 2 ** Math.min(attempt, 5))));
    };
    connect();
    return () => { closed = true; if (timer) window.clearTimeout(timer); ws?.close(); };
  }, [store, viewing]);
}
